import {ApolloLink, from} from '@apollo/client'
import {useApolloClient} from '@vue/apollo-composable'
import {GetMeDocument} from '~/graphql/queries/me.generated'
import type {Customer} from '~/generated/types'
import {
  UpdateCustomerDocument,
  type UpdateCustomerMutation,
  type UpdateCustomerMutationVariables,
} from "~/graphql/mutations/customer.generated";

// 타입 정의
interface AuthResponse {
  success: boolean
  data?:
    | {
    accessToken: string
    refreshToken: string
    expiresIn: number
  }
    | null
    | undefined
}

export function useAuth() {
  const config = useRuntimeConfig()
  const {client} = useApolloClient()

  // 상태 관리
  const user = useState<Customer | null | undefined>('user', () => null)
  const error = useState<string | null>('auth-error', () => null)
  const isAuthenticated = computed(() => !!user.value)
  const checking = useState<boolean>('auth-checking', () => true)

  const {mutate: updateCustomer} = useMutation<UpdateCustomerMutation, UpdateCustomerMutationVariables>(
    UpdateCustomerDocument
  )

  // 토큰 관리 유틸리티
  const tokenUtils = {
    getStoreKey: (storeId: string) => `store_${storeId}_token`,

    setToken: (storeId: string, token: string) => {
      if (import.meta.client) {
        localStorage.setItem(tokenUtils.getStoreKey(storeId), token)
      }
    },

    getToken: (storeId: string) => {
      if (import.meta.client) {
        return localStorage.getItem(tokenUtils.getStoreKey(storeId))
      }
      return null
    },

    removeToken: (storeId: string) => {
      if (import.meta.client) {
        localStorage.removeItem(tokenUtils.getStoreKey(storeId))
      }
    },
  }

  // Apollo Client 설정 관리
  const apolloUtils = {
    setAuthHeader: async (token: string | null) => {
      const authLink = new ApolloLink((operation, forward) => {
        operation.setContext({
          headers: {
            authorization: token ? `Bearer ${token}` : '',
          },
        })
        return forward(operation)
      })

      try {
        // 스토어 초기화 전에 모든 쿼리가 완료될 때까지 대기
        await new Promise(resolve => setTimeout(resolve, 100))
        await client.clearStore()
        client.setLink(from([authLink, client.link]))
      } catch (e) {
        console.error('Error setting auth header:', e)
        // 실패 시 링크만 설정
        client.setLink(from([authLink, client.link]))
      }
    },

    fetchUserData: async () => {
      try {
        const {data} = await client.query({
          query: GetMeDocument,
          fetchPolicy: 'network-only',
        })
        return data.me as Customer | null
      } catch {
        return null
      }
    },
  }

  // 에러 처리
  const handleError = (message: string) => {
    error.value = message
    setTimeout(() => (error.value = null), 3000)
  }

  // 주요 기능
  const auth = {
    register: async (params: {
      storeId: string
      email: string
      password: string
    }) => {
      try {
        const response = await $fetch('/auth/register/email', {
          baseURL: config.public.apiBase,
          method: 'POST',
          body: {
            storeId: params.storeId,
            email: params.email,
            password: params.password,
            role: 'CUSTOMER',
          },
          headers: {
            'Content-Type': 'application/json',
          },
        })

        console.log('Register response', response)
      } catch (err: any) {
        handleError(err.message || '회원가입 중 오류가 발생했습니다')
        throw err
      }
    },
    // 로그인
    login: async (storeId: string, email: string, password: string) => {
      try {
        const response: AuthResponse = await $fetch('/auth/login/email', {
          baseURL: config.public.apiBase,
          method: 'POST',
          body: {
            storeId,
            email,
            password,
            role: 'CUSTOMER',
          },
        })
        if (!response.success || !response.data?.accessToken) {
          throw new Error('로그인에 실패했습니다')
        }

        // 토큰 저장
        tokenUtils.setToken(storeId, response.data.accessToken)
        await apolloUtils.setAuthHeader(response.data.accessToken)


        const temp = localStorage.getItem(`${storeId}_temp_user`)

        if (temp) {
          const user = JSON.parse(temp)

          if (user.email === email) {
            await updateCustomer({
              input: {
                name: user.name,
                phone: user.phone,
                defaultShippingAddress: {
                  recipientName: user.name,
                  recipientPhone: user.phone,
                  ...user.address
                }
              }
            })

          }
        }

        // 사용자 정보 조회
        const userData = await apolloUtils.fetchUserData()
        if (userData) {
          user.value = userData
          localStorage.removeItem(`${storeId}_temp_user`)
        } else {
          throw new Error('사용자 정보를 가져올 수 없습니다')
        }
      } catch (err: any) {
        handleError(err.message || '로그인 중 오류가 발생했습니다')
        throw err
      }
    },

    // 로그아웃
    logout: async (storeId: string) => {
      tokenUtils.removeToken(storeId)
      await apolloUtils.setAuthHeader(null)
      user.value = null
    },

    // 인증 상태 확인
    // In useAuth.ts
    checkAuth: async (storeId: string): Promise<boolean> => {
      try {
        checking.value = true
        const token = tokenUtils.getToken(storeId)

        if (!token) {
          checking.value = false
          return false
        }

        await apolloUtils.setAuthHeader(token)

        // Add a retry mechanism
        let retries = 2
        let userData = null

        while (retries > 0) {
          try {
            userData = await apolloUtils.fetchUserData()
            break
          } catch (e) {
            retries--
            if (retries === 0) throw e
            // Wait before retrying
            await new Promise(r => setTimeout(r, 500))
          }
        }

        if (userData) {
          user.value = userData
          return true
        }

        await auth.logout(storeId)
        return false
      } catch (e) {
        console.error('Auth check failed:', e)
        await auth.logout(storeId)
        return false
      } finally {
        checking.value = false
      }
    },
    refreshUser: async () => {
      try {
        const userData = await apolloUtils.fetchUserData()
        if (userData) {
          user.value = userData
        }
      } catch (e) {
        console.error('Failed to refresh user data', e)
      }
    }
  }

  return {
    user,
    error,
    isAuthenticated,
    ...auth,
    checking,
    tokenUtils,
  }
}
